import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import type { ThreadMessage } from '@/types/messages';
import type { TodoItemData } from '@/types';
import { extractPlanDataFromMessages } from '@/utils/plan.utils';
import {
  EXECUTE_PLAN_COMMAND_PREFIX,
  UNKNOWN_PLAN_ID,
} from '@/constants/ui.constants';
import { logger } from '@/utils/logger';

export interface PlanData {
  id: string;
  planText: string;
  todos: TodoItemData[];
}

interface PlanModeContextValue {
  currentPlan: PlanData | null;
  setCurrentPlan: (plan: PlanData | null) => void;
  executePlan: (
    sendMessage?: (content: string) => Promise<void>
  ) => Promise<void>;
  isExecuting: boolean;
  updateTodoStatus: (
    todoContent: string,
    status: TodoItemData['status']
  ) => void;
  extractAndSetPlan: (messages: ThreadMessage[]) => void;
  clearPlan: () => void;
  setSendMessage: (
    sendMessage: (content: string) => Promise<void>
  ) => void;
}

const PlanModeContext = createContext<
  PlanModeContextValue | undefined
>(undefined);

export const usePlanMode = (): PlanModeContextValue => {
  const context = useContext(PlanModeContext);
  if (!context) {
    throw new Error(
      'usePlanMode must be used within a PlanModeProvider'
    );
  }
  return context;
};

/**
 * Optional version of usePlanMode that returns null if not within provider.
 * Useful for components that may or may not have PlanModeProvider as ancestor.
 */
export const usePlanModeOptional = (): PlanModeContextValue | null => {
  return useContext(PlanModeContext) ?? null;
};

interface PlanModeProviderProps {
  children: ReactNode;
}

export const PlanModeProvider: React.FC<
  PlanModeProviderProps
> = ({ children }) => {
  const [currentPlan, setCurrentPlan] =
    useState<PlanData | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const sendMessageRef = useRef<
    ((content: string) => Promise<void>) | null
  >(null);

  const setSendMessage = useCallback(
    (sendMessage: (content: string) => Promise<void>) => {
      sendMessageRef.current = sendMessage;
    },
    []
  );

  const executePlan = useCallback(
    async (sendMessage?: (content: string) => Promise<void>) => {
      if (isExecuting || !currentPlan || !currentPlan.id) {
        return;
      }

      setIsExecuting(true);
      try {
        const sendMessageFn = sendMessage || sendMessageRef.current;
        if (sendMessageFn) {
          // Use plan ID from current plan context (backend creates the ID)
          await sendMessageFn(
            `${EXECUTE_PLAN_COMMAND_PREFIX} ${currentPlan.id}`
          );
        }
      } catch (error) {
        // Error handling: log but don't throw to avoid breaking UI
        logger.error('Failed to execute plan:', error);
      } finally {
        setIsExecuting(false);
      }
    },
    [currentPlan, isExecuting]
  );

  const updateTodoStatus = useCallback(
    (todoContent: string, status: TodoItemData['status']) => {
      setCurrentPlan((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          todos: prev.todos.map((todo) =>
            todo.content === todoContent ? { ...todo, status } : todo
          ),
        };
      });
    },
    []
  );

  const extractAndSetPlan = useCallback(
    (messages: ThreadMessage[]) => {
      const extracted = extractPlanDataFromMessages(messages);
      if (!extracted) {
        return;
      }

      // Return plan data if we have planText or todos
      if (extracted.planText || extracted.todos.length > 0) {
        // Use messageId from extracted data, or fallback to planId, or first message ID
        const messageId =
          extracted.messageId ||
          extracted.planId ||
          (messages.length > 0 ? messages[0].id : UNKNOWN_PLAN_ID);
        const planId = extracted.planId || messageId;
        setCurrentPlan({
          id: planId,
          planText: extracted.planText || '',
          todos: extracted.todos,
        });
      }
    },
    []
  );

  const clearPlan = useCallback(() => {
    setCurrentPlan(null);
  }, []);

  const value: PlanModeContextValue = {
    currentPlan,
    setCurrentPlan,
    executePlan,
    isExecuting,
    updateTodoStatus,
    extractAndSetPlan,
    clearPlan,
    setSendMessage,
  };

  return (
    <PlanModeContext.Provider value={value}>
      {children}
    </PlanModeContext.Provider>
  );
};

